import React, { useState } from "react";
import { motion } from "framer-motion";
import { InView, EyebrowLabel } from "../utils/Animation";
import { useSubmitContactMutation } from "../app/api/contactApi";
import type { ProjectType, BudgetRange } from "../app/api/contactApi";

// ─── Types ────────────────────────────────────────────────────────────
interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  projectType: ProjectType;
  budget: BudgetRange;
  timeline: string;
  message: string;
}

// ─── Data ─────────────────────────────────────────────────────────────
const PROJECT_TYPES: Exclude<ProjectType, "">[] = [
  "Web Development",
  "Mobile App",
  "Tech Product",
  "Consulting",
  "Other",
];

const BUDGETS: Exclude<BudgetRange, "">[] = [
  "< \u20a61M",
  "\u20a61M \u2013 \u20a65M",
  "\u20a65M \u2013 \u20a620M",
  "\u20a620M+",
  "Let's discuss",
];

const CONTACT_INFO = [
  {
    label: "Email",
    value: "hello@incubatortech.org",
    href: "mailto:hello@incubatortech.org",
  },
  {
    label: "Phone",
    value: "+234 (0) 705 249 9758",
    href: "tel:+2347052499758",
  },
  {
    label: "Kurudu Office",
    value: "No 2, J.S. Malu Street, NAPSHS, Abuja",
    href: "#",
  },
  // {
  //   label: "Wuse Office",
  //   value: "Plot 1234, Wuse Zone 5, Abuja",
  //   href: "#",
  // },
];

// ─── Field wrapper ────────────────────────────────────────────────────
function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
        {label}
        {required && <span className="text-itl-blue ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  "w-full bg-transparent border-b border-gray-200 py-3 text-[14px] text-itl-navy placeholder-gray-300 focus:outline-none focus:border-itl-navy transition-colors duration-200";

// ─── Page ─────────────────────────────────────────────────────────────
export default function Contact() {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    company: "",
    phone: "",
    projectType: "",
    budget: "",
    timeline: "",
    message: "",
  });

  // RTK Query mutation
  const [submitContact, { isLoading, isSuccess, isError, error }] =
    useSubmitContactMutation();

  function update(key: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        company: form.company || undefined,
        phone: form.phone || undefined,
        projectType: form.projectType || undefined,
        budget: form.budget || undefined,
        timeline: form.timeline || undefined,
        message: form.message,
      }).unwrap();
    } catch {
      // isError handles the UI state — no local state needed
    }
  }

  // Derive a user-facing error message from the RTK error object
  const errorMessage = (() => {
    if (!error) return null;
    if ("data" in error) {
      const data = error.data as { error?: string };
      return data?.error ?? "Something went wrong. Please try again.";
    }
    return "Network error. Please check your connection and try again.";
  })();

  return (
    <>
      {/* ── Hero ── */}
      <div className="relative bg-itl-navy pt-40 pb-32 overflow-hidden">
        <div className="absolute left-6 lg:left-12 top-0 bottom-0 flex items-center pointer-events-none">
          <div className="h-[35%] w-px bg-white/10" />
        </div>
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-4"
          >
            Contact Us
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.85,
              ease: [0.22, 1, 0.36, 1],
              delay: 0.25,
            }}
            className="font-serif text-5xl lg:text-7xl font-light text-white max-w-3xl leading-[1.06] mb-6"
          >
            Let's start a conversation.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-white/55 text-lg max-w-lg leading-relaxed"
          >
            Whether you have a fully-scoped project or just a raw idea, we'd
            love to hear about it. We respond to all enquiries within one
            business day.
          </motion.p>
        </div>
      </div>

      {/* ── Main content ── */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-16 lg:gap-24">
          {/* ── FORM COLUMN ── */}
          <div>
            <InView className="mb-10">
              <EyebrowLabel>Send Us a Brief</EyebrowLabel>
              <h2 className="font-serif text-3xl font-light text-itl-navy leading-snug">
                Tell us about your project.
              </h2>
            </InView>

            {/* ── Success state ── */}
            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="py-16 px-10 text-center border border-green-100 bg-green-50"
              >
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-5">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="font-serif text-2xl font-light text-itl-navy mb-3">
                  Message received.
                </h3>
                <p className="text-gray-500 text-[14px] leading-relaxed max-w-sm mx-auto">
                  We'll review your brief and come back to you within one
                  business day. Check your inbox — a confirmation email is on
                  its way.
                </p>
              </motion.div>
            ) : (
              <InView delay={0.05}>
                <form onSubmit={handleSubmit} className="flex flex-col gap-8">
                  {/* Name + Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <Field label="Full Name" required>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        required
                        placeholder="Adeolu Adegbemi"
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Email Address" required>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        required
                        placeholder="emeka@company.ng"
                        className={inputClass}
                      />
                    </Field>
                  </div>

                  {/* Company + Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <Field label="Company / Organisation">
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        placeholder="Acme Fintech Ltd"
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Phone Number">
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+234 812 345 6789"
                        className={inputClass}
                      />
                    </Field>
                  </div>

                  {/* Project type */}
                  <Field label="Type of Project">
                    <div className="flex flex-wrap gap-2 mt-1">
                      {PROJECT_TYPES.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => update("projectType", type)}
                          className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-widest border transition-colors duration-150 ${
                            form.projectType === type
                              ? "bg-itl-navy border-itl-navy text-white"
                              : "border-gray-200 text-gray-500 hover:border-itl-navy hover:text-itl-navy"
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </Field>

                  {/* Budget */}
                  <Field label="Budget Range">
                    <div className="flex flex-wrap gap-2 mt-1">
                      {BUDGETS.map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => update("budget", b)}
                          className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-widest border transition-colors duration-150 ${
                            form.budget === b
                              ? "bg-itl-blue border-itl-blue text-white"
                              : "border-gray-200 text-gray-500 hover:border-itl-blue hover:text-itl-blue"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </Field>

                  {/* Timeline */}
                  <Field label="Desired Timeline">
                    <input
                      type="text"
                      value={form.timeline}
                      onChange={(e) => update("timeline", e.target.value)}
                      placeholder="e.g. Launch in Q3 2025"
                      className={inputClass}
                    />
                  </Field>

                  {/* Message */}
                  <Field label="Project Brief" required>
                    <textarea
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      required
                      rows={5}
                      placeholder="Tell us what you're building, the problem you're solving, and any constraints we should know about..."
                      className={`${inputClass} resize-none`}
                    />
                  </Field>

                  {/* Error banner */}
                  {isError && errorMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="px-5 py-4 bg-red-50 border border-red-100 text-red-700 text-[13px] leading-relaxed"
                    >
                      {errorMessage}
                    </motion.div>
                  )}

                  {/* Submit */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-10 py-4 bg-itl-navy text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-itl-blue transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-3"
                    >
                      {isLoading ? (
                        <>
                          <svg
                            className="animate-spin w-4 h-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            />
                          </svg>
                          Sending…
                        </>
                      ) : (
                        "Send Brief →"
                      )}
                    </button>
                    <p className="text-gray-400 text-[11px] mt-4">
                      We respond to all enquiries within one business day.
                    </p>
                  </div>
                </form>
              </InView>
            )}
          </div>

          {/* ── SIDEBAR ── */}
          <div className="flex flex-col gap-12">
            {/* Contact details */}
            <InView delay={0.1}>
              <EyebrowLabel>Get In Touch Directly</EyebrowLabel>
              <div className="flex flex-col gap-6">
                {CONTACT_INFO.map((c) => (
                  <div key={c.label}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400 mb-1">
                      {c.label}
                    </p>
                    <a
                      href={c.href}
                      className="text-[14px] text-itl-navy hover:text-itl-blue transition-colors"
                    >
                      {c.value}
                    </a>
                  </div>
                ))}
              </div>
            </InView>

            {/* What happens next */}
            <InView delay={0.15}>
              <div className="bg-[#f7f7f5] p-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400 mb-6">
                  What Happens Next
                </p>
                <div className="flex flex-col gap-6">
                  {[
                    {
                      step: "1",
                      text: "We review your brief and do initial research on your market and requirements.",
                    },
                    {
                      step: "2",
                      text: "We schedule a 30-minute discovery call to ask the right questions.",
                    },
                    {
                      step: "3",
                      text: "We send a clear proposal: approach, timeline, and fixed-price quote.",
                    },
                  ].map((s) => (
                    <div key={s.step} className="flex gap-4 items-start">
                      <span className="w-7 h-7 bg-itl-blue text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                        {s.step}
                      </span>
                      <p className="text-gray-600 text-[13px] leading-relaxed">
                        {s.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </InView>

            {/* Social links */}
            <InView delay={0.2}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400 mb-4">
                Follow Us
              </p>
              <div className="flex flex-col gap-3">
                {[
                  { name: "LinkedIn", href: "#" },
                  { name: "Twitter / X", href: "#" },
                  { name: "GitHub", href: "#" },
                ].map((s) => (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between text-[13px] text-itl-navy hover:text-itl-blue transition-colors group"
                  >
                    {s.name}
                    <span className="group-hover:translate-x-1 transition-transform">
                      →
                    </span>
                  </a>
                ))}
              </div>
            </InView>
          </div>
        </div>
      </section>

      {/* ── Office section ── */}
      <section className="bg-[#f7f7f5] py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-8">
            <EyebrowLabel>Our Offices</EyebrowLabel>
          </InView>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200">
            {[
              // {
              //   city: "Lagos",
              //   address: "14 Admiralty Way, Lekki Phase 1",
              //   country: "Nigeria",
              //   image: "/images/hero.png",
              // },
              {
                city: "Abuja",
                address: "No 2, J.S. Malu Street, NAPSHS, Kurudu",
                country: "Nigeria",
                image: "/images/africa.jpg",
              },
            ].map((office, i) => (
              <InView key={office.city} delay={i * 0.08} className="bg-white">
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={office.image}
                    alt={office.city}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-itl-navy/30" />
                  <div className="absolute bottom-4 left-4">
                    <p className="font-serif text-2xl font-light text-white">
                      {office.city}
                    </p>
                  </div>
                </div>
                <div className="p-7">
                  <p className="text-[14px] text-gray-600">{office.address}</p>
                  <p className="text-[12px] font-semibold uppercase tracking-widest text-gray-400 mt-1">
                    {office.country}
                  </p>
                </div>
              </InView>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
